import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { toast } from '../shared/Toast';
import { v4 as uuidv4 } from 'uuid';
import DebtSimulator from './DebtSimulator';

const DEBT_TYPES = [
  { value: 'Empréstimo',       icon: 'fa-building-columns' },
  { value: 'Financiamento',    icon: 'fa-file-invoice' },
  { value: 'Cartão de Crédito',icon: 'fa-credit-card' },
  { value: 'Outro',            icon: 'fa-circle-dollar-to-slot' },
];

const emptyForm = {
  name: '', type: 'Empréstimo', total: '', remaining: '',
  installment: '', interestRate: '', dueDay: '',
  startDate: '', endDate: '', notes: '',
};

export function PayDebtModal({ debt, accounts, onClose }) {
  const { dispatch } = useApp();
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [amount, setAmount]       = useState(String(debt.installment || ''));
  const [abatement, setAbatement] = useState('0');
  const [date, setDate]           = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote]           = useState('');
  const [quitarTotal, setQuitarTotal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // val  = valor da parcela / nominal que ABATE a dívida
  // abat = desconto recebido (ex: por pagar adiantado) — NÃO sai da conta
  // cashOut = o que realmente sai da conta = parcela − desconto
  const val     = parseFloat(amount)    || 0;
  const abat     = Math.min(parseFloat(abatement) || 0, val);
  const cashOut  = Math.max(0, val - abat);           // débito real na conta
  const reduction = quitarTotal ? debt.remaining : val; // quanto some da dívida
  const newRemaining = quitarTotal ? 0 : Math.max(0, debt.remaining - reduction);
  const payments     = debt.payments || [];

  const confirm = () => {
    if (!accountId) { toast.error('Selecione uma conta.'); return; }
    if (val <= 0)   { toast.error('Informe o valor do pagamento.'); return; }
    if (!date)      { toast.error('Informe a data do pagamento.'); return; }

    // Só sai da conta o que você efetivamente pagou (parcela − desconto).
    const txId = uuidv4();
    dispatch({ type: 'ADD_TRANSACTION', payload: {
      id: txId, type: 'expense', accountId, value: cashOut,
      description: `Pagamento: ${debt.name}`,
      date, status: 'paid', categoryId: 'contas',
    }});

    // Guarda txId + reduction pra permitir desfazer o pagamento com exatidão depois.
    const newPayment = { id: uuidv4(), date, amount: val, abatement: abat, cashOut, reduction, txId, accountId, note: note.trim() };

    dispatch({ type: 'UPDATE_DEBT', payload: {
      ...debt,
      remaining: newRemaining,
      settled: newRemaining <= 0,
      payments: [...payments, newPayment],
    }});

    toast.success(newRemaining <= 0 ? 'Dívida quitada!' : `Baixa de ${formatCurrency(reduction)} registrada!`);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          Registrar Pagamento
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Resumo da dívida */}
        <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Dívida</div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{debt.name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Saldo devedor atual: <strong style={{ color: '#e24b4a' }}>{formatCurrency(debt.remaining)}</strong>
            {debt.installment > 0 && <span style={{ marginLeft: 10 }}>· Parcela: <strong>{formatCurrency(debt.installment)}</strong></span>}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Conta de Débito</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name} — {formatCurrency(a.balance)}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor da Parcela (R$)</label>
            <input className="form-control" type="number" min="0" step="0.01"
              value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" />
          </div>
          <div className="form-group">
            <label className="form-label">Abatimento / desconto (R$)</label>
            <input className="form-control" type="number" min="0" step="0.01"
              value={abatement} onChange={e => setAbatement(e.target.value)} placeholder="0,00" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Data do Pagamento</label>
          <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Observação (opcional)</label>
          <input className="form-control" value={note} onChange={e => setNote(e.target.value)} placeholder="Ex: Parcela de junho..." />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, fontSize: 13 }}>
          <input type="checkbox" checked={quitarTotal} onChange={e => setQuitarTotal(e.target.checked)} />
          Quitar dívida completamente
        </label>

        {/* Preview do resultado */}
        <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: 'var(--muted)' }}>Sai da conta:</span>
            <span style={{ color: '#e24b4a', fontWeight: 600 }}>{formatCurrency(cashOut)}</span>
          </div>
          {abat > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--muted)' }}>Desconto (abatimento):</span>
              <span style={{ color: '#1d9e75', fontWeight: 600 }}>− {formatCurrency(abat)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: 'var(--muted)' }}>Abate da dívida:</span>
            <span style={{ color: '#1d9e75', fontWeight: 600 }}>{formatCurrency(reduction)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }}>
            <span style={{ color: 'var(--muted)' }}>Novo saldo devedor:</span>
            <strong style={{ color: newRemaining <= 0 ? '#1d9e75' : '#e24b4a' }}>{formatCurrency(newRemaining)}</strong>
          </div>
        </div>

        {/* Histórico de pagamentos */}
        {payments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => setShowHistory(h => !h)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--blue)', fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <i className={`fas fa-chevron-${showHistory ? 'up' : 'down'}`} style={{ fontSize: 10 }}></i>
              Histórico de pagamentos ({payments.length})
            </button>
            {showHistory && (
              <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {[...payments].sort((a, b) => b.date.localeCompare(a.date)).map((p, i) => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', fontSize: 12,
                    background: i % 2 === 0 ? 'var(--surface2)' : 'var(--card-bg)',
                    borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                        {new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </div>
                      {p.note && <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 1 }}>{p.note}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: '#1d9e75' }}>{formatCurrency(p.amount)}</div>
                      {p.abatement > 0 && <div style={{ fontSize: 10, color: 'var(--muted)' }}>Abat: {formatCurrency(p.abatement)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-full" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary btn-full" onClick={confirm} style={{ padding: 12 }}>
            <i className="fas fa-check"></i> Confirmar Pagamento
          </button>
        </div>
      </div>
    </div>
  );
}

function DebtModal({ debt, onClose }) {
  const { dispatch } = useApp();
  const [form, setForm] = useState(debt
    ? { ...debt, total: String(debt.total), remaining: String(debt.remaining), installment: String(debt.installment), interestRate: String(debt.interestRate) }
    : { ...emptyForm }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório.'); return; }
    if (!form.total || parseFloat(form.total) <= 0) { toast.error('Valor total deve ser maior que zero.'); return; }
    if (!form.dueDay || form.dueDay < 1 || form.dueDay > 31) { toast.error('Informe um dia de vencimento válido (1–31).'); return; }
    const payload = {
      ...form,
      total:        parseFloat(form.total)        || 0,
      remaining:    parseFloat(form.remaining)    || parseFloat(form.total) || 0,
      installment:  parseFloat(form.installment)  || 0,
      interestRate: parseFloat(form.interestRate) || 0,
      dueDay:       parseInt(form.dueDay)         || 1,
    };
    if (debt) {
      dispatch({ type: 'UPDATE_DEBT', payload });
    } else {
      dispatch({ type: 'ADD_DEBT', payload: { id: uuidv4(), settled: false, ...payload } });
      // Guarda-costas "na hora": dívida nova entra no radar do "não sair da rota"
      dispatch({ type: 'ADD_NOTIFICATION', payload: { title: '➕ Dívida nova no radar', message: `${payload.name || 'Dívida'}${payload.installment ? ` — parcela R$ ${payload.installment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}. Foco em quitar! 💪`, type: 'warning' } });
    }
    toast.success(debt ? 'Dívida atualizada!' : 'Dívida cadastrada!');
    onClose();
  };

  const selectedType = DEBT_TYPES.find(t => t.value === form.type) || DEBT_TYPES[0];

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          {debt ? 'Editar Dívida' : 'Nova Dívida'}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label className="form-label">Nome</label>
          <input className="form-control" autoFocus value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Financiamento do Carro" />
        </div>

        <div className="form-group">
          <label className="form-label">Tipo</label>
          <select className="form-control" value={form.type} onChange={e => set('type', e.target.value)}>
            {DEBT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.value}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor Total</label>
            <input className="form-control" type="number" min="0" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} placeholder="0" />
          </div>
          <div className="form-group">
            <label className="form-label">Valor Restante</label>
            <input className="form-control" type="number" min="0" step="0.01" value={form.remaining} onChange={e => set('remaining', e.target.value)} placeholder="0" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Parcela Mensal</label>
            <input className="form-control" type="number" min="0" step="0.01" value={form.installment} onChange={e => set('installment', e.target.value)} placeholder="0,00" />
          </div>
          <div className="form-group">
            <label className="form-label">Taxa de Juros (%)</label>
            <input className="form-control" type="number" min="0" step="0.01" value={form.interestRate} onChange={e => set('interestRate', e.target.value)} placeholder="0,00" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Dia de Vencimento</label>
          <input className="form-control" type="number" min="1" max="31" value={form.dueDay} onChange={e => set('dueDay', e.target.value)} placeholder="1–31" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Data de Início</label>
            <input className="form-control" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Data de Término</label>
            <input className="form-control" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Observações</label>
          <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Informações adicionais..." style={{ resize: 'none' }} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-full" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}>
            <i className="fas fa-check"></i> {debt ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Debts() {
  const { state, dispatch } = useApp();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payingDebt, setPayingDebt] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});

  const toggleHistory = (id) => setExpandedHistory(prev => ({ ...prev, [id]: !prev[id] }));

  const debts = state.debts || [];
  const activeAccounts = useMemo(() => state.accounts.filter(a => a.active), [state.accounts]);
  const totalDebt = debts.reduce((s, d) => s + (d.remaining || 0), 0);
  const totalOriginal = debts.reduce((s, d) => s + (d.total || 0), 0);
  const totalSettled = debts.reduce((s, d) => s + (d.settled ? (d.total || 0) : 0), 0);
  const pctSettled = totalOriginal > 0 ? Math.round((totalSettled / totalOriginal) * 100) : 0;

  const toggleSettled = (debt) => {
    dispatch({ type: 'UPDATE_DEBT', payload: { ...debt, settled: !debt.settled } });
  };

  const remove = (id) => {
    if (!window.confirm('Remover esta dívida?')) return;
    dispatch({ type: 'DELETE_DEBT', payload: id });
    toast.success('Dívida removida.');
  };

  // Desfaz uma baixa: apaga o lançamento na conta (devolvendo o dinheiro) e
  // devolve o valor abatido ao saldo devedor.
  const undoPayment = (debt, payment) => {
    if (!window.confirm('Desfazer este pagamento? O valor volta para a conta e para o saldo da dívida.')) return;

    // Quanto foi abatido da dívida na época: registros novos guardam `reduction`;
    // os antigos usavam a fórmula (valor − abatimento).
    const restore = payment.reduction != null
      ? payment.reduction
      : Math.max(0, (payment.amount || 0) - (payment.abatement || 0));

    // Apaga o lançamento vinculado (devolve o saldo à conta). Registro novo tem
    // txId; nos antigos, casa pelo lançamento "Pagamento: {dívida}" de mesmo
    // valor e data — só apaga se houver UM único correspondente (evita enganos).
    let txRemoved = false;
    if (payment.txId && state.transactions.some(t => t.id === payment.txId)) {
      dispatch({ type: 'DELETE_TRANSACTION', payload: payment.txId });
      txRemoved = true;
    } else {
      const matches = state.transactions.filter(t =>
        t.description === `Pagamento: ${debt.name}` &&
        t.date === payment.date &&
        Math.abs((t.value || 0) - (payment.amount || 0)) < 0.005
      );
      if (matches.length === 1) {
        dispatch({ type: 'DELETE_TRANSACTION', payload: matches[0].id });
        txRemoved = true;
      }
    }

    const newRemaining = Math.max(0, (debt.remaining || 0) + restore);
    dispatch({ type: 'UPDATE_DEBT', payload: {
      ...debt,
      remaining: newRemaining,
      settled: false,
      payments: (debt.payments || []).filter(p => p.id !== payment.id),
    }});

    toast.success(txRemoved
      ? 'Pagamento desfeito — saldo devolvido à conta e à dívida.'
      : 'Pagamento desfeito na dívida. Confira o lançamento na conta manualmente.');
  };

  const typeIcon = (type) => (DEBT_TYPES.find(t => t.value === type) || DEBT_TYPES[3]).icon;

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Dívidas</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Gerencie seus passivos e financiamentos</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
          <i className="fas fa-plus"></i> Nova Dívida
        </button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Total de Dívidas</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--red)', marginTop: 6 }}>{formatCurrency(totalDebt)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{pctSettled}% já quitado</div>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label">Parcelas / mês</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 6 }}>
            {formatCurrency(debts.filter(d => !d.settled).reduce((s, d) => s + (d.installment || 0), 0))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{debts.filter(d => !d.settled).length} dívida(s) ativa(s)</div>
        </div>
      </div>

      {/* Simulador de quitação — bola de neve / avalanche */}
      <DebtSimulator />

      {/* Debt list */}
      <div className="card">
        <div className="section-title">
          <i className="fas fa-file-invoice-dollar" style={{ marginRight: 8, color: 'var(--gold)' }}></i>
          Suas Dívidas
        </div>

        {debts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <i className="fas fa-hand-holding-dollar" style={{ fontSize: 36, opacity: 0.3, display: 'block', marginBottom: 12 }}></i>
            <div style={{ fontSize: 14 }}>Nenhuma dívida cadastrada</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Cadastre empréstimos, financiamentos e outros passivos</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {debts.map(debt => {
              const pct = debt.total > 0 ? Math.round(((debt.total - debt.remaining) / debt.total) * 100) : 0;
              return (
                <div key={debt.id} style={{
                  border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden',
                  opacity: debt.settled ? 0.6 : 1,
                }}>
                  {/* Top row */}
                  <div style={{ padding: '14px 16px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: debt.settled ? 'rgba(29,158,117,0.15)' : 'rgba(226,75,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`fas ${typeIcon(debt.type)}`} style={{ fontSize: 16, color: debt.settled ? '#1d9e75' : '#e24b4a' }}></i>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {debt.name}
                        {debt.settled && <span className="tag tag-green" style={{ fontSize: 10 }}>Quitado</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {debt.type}
                        {debt.dueDay ? ` · Vence dia ${debt.dueDay}` : ''}
                        {debt.interestRate > 0 ? ` · ${debt.interestRate}% a.m.` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 15, color: debt.settled ? '#1d9e75' : 'var(--red)' }}>{formatCurrency(debt.remaining)}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>restante</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {debt.total > 0 && (
                    <div style={{ padding: '8px 16px', background: 'var(--card-bg)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                        <span>Pago: {formatCurrency(debt.total - debt.remaining)}</span>
                        <span>Total: {formatCurrency(debt.total)}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 99, width: `${pct}%`, background: pct >= 100 ? '#1d9e75' : pct >= 50 ? 'var(--gold)' : '#e24b4a', transition: 'width 0.4s' }} />
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{pct}% pago</div>
                    </div>
                  )}

                  {/* Histórico de baixas */}
                  {(debt.payments || []).length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--card-bg)' }}>
                      <button
                        onClick={() => toggleHistory(debt.id)}
                        style={{ width: '100%', padding: '7px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--blue)', fontWeight: 600 }}
                      >
                        <i className={`fas fa-chevron-${expandedHistory[debt.id] ? 'up' : 'down'}`} style={{ fontSize: 9 }}></i>
                        Histórico de pagamentos ({debt.payments.length})
                        <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontWeight: 400 }}>
                          Total pago: {formatCurrency(debt.payments.reduce((s, p) => s + p.amount, 0))}
                        </span>
                      </button>
                      {expandedHistory[debt.id] && (
                        <div style={{ borderTop: '1px solid var(--border)' }}>
                          {[...debt.payments].sort((a, b) => b.date.localeCompare(a.date)).map((p, i) => (
                            <div key={p.id} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '7px 16px', fontSize: 12,
                              background: i % 2 === 0 ? 'var(--surface2)' : 'var(--card-bg)',
                              borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="fas fa-circle-check" style={{ color: '#1d9e75', fontSize: 11 }}></i>
                                <div>
                                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                                    {new Date(p.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                                  </div>
                                  {p.note && <div style={{ color: 'var(--muted)', fontSize: 10 }}>{p.note}</div>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 700, color: '#1d9e75' }}>{formatCurrency(p.amount)}</div>
                                  {p.abatement > 0 && <div style={{ fontSize: 10, color: 'var(--muted)' }}>Abat: {formatCurrency(p.abatement)}</div>}
                                </div>
                                <button
                                  onClick={() => undoPayment(debt, p)}
                                  title="Desfazer este pagamento"
                                  style={{ padding: '4px 7px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 11, color: '#ef4444', flexShrink: 0 }}
                                >
                                  <i className="fas fa-rotate-left"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Footer row */}
                  <div style={{ padding: '8px 16px', background: 'var(--card-bg)', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {debt.installment > 0 && <span>Parcela: <strong style={{ color: 'var(--text)' }}>{formatCurrency(debt.installment)}/mês</strong></span>}
                      {debt.endDate && <span style={{ marginLeft: 10 }}>Término: <strong style={{ color: 'var(--text)' }}>{new Date(debt.endDate + 'T00:00:00').toLocaleDateString('pt-BR')}</strong></span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {debt.settled ? (
                        <button
                          onClick={() => toggleSettled(debt)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 11, color: '#e24b4a', fontWeight: 600 }}
                        >
                          Reabrir
                        </button>
                      ) : (
                        <button
                          onClick={() => setPayingDebt(debt)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #1d9e75', background: 'rgba(29,158,117,0.08)', cursor: 'pointer', fontSize: 11, color: '#1d9e75', fontWeight: 600 }}
                        >
                          <i className="fas fa-money-bill-wave" style={{ marginRight: 4 }}></i>Baixar
                        </button>
                      )}
                      <button
                        onClick={() => { setEditing(debt); setModal(true); }}
                        style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--muted)' }}
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                      <button
                        onClick={() => remove(debt.id)}
                        style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 11, color: '#ef4444' }}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && <DebtModal debt={editing} onClose={() => { setModal(false); setEditing(null); }} />}
      {payingDebt && <PayDebtModal debt={payingDebt} accounts={activeAccounts} onClose={() => setPayingDebt(null)} />}
    </div>
  );
}
